import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery, keepPreviousData, replaceEqualDeep} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  ServerUnavailableError,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {sendStats} from '@github-ui/stats'
import {useEffect, useMemo} from 'react'
import type {CheckRunForAnalysis, CIFindingsResponse, DedupedCIFinding} from './ci-findings-types'
import {
  adaptCIFindingsResponse,
  computeAnalysisProgress,
  hasNormalizedLifecycle,
  isCIFindingsAnalysisPending,
  mergeCIFindingsData,
  shouldPollForCIFindings,
  type AnalysisProgress,
  type CIFindingsData,
} from './ci-findings-data'
import {deduplicateCIFindings} from './deduplicate-ci-findings'
import {useAnalysisPollingTimeout} from './use-analysis-polling-timeout'

export type {AnalysisProgress} from './ci-findings-data'

export const POLL_INTERVAL_MS = 10_000
export const TERMINAL_REFETCH_INTERVAL_MS = 30_000
export const MAX_POLL_DURATION_MS = 2 * 60 * 60 * 1000 // 2 hours — CI can take a long time
const EMPTY_DATA: CIFindingsData = {
  findings: [],
  headSha: undefined,
  checkRunsForAnalysis: undefined,
  resultsReady: undefined,
  jobStatus: undefined,
}
const EMPTY_CHECK_RUNS: CheckRunForAnalysis[] = []

export type CIFindingsState = {
  findings: DedupedCIFinding[]
  headSha: string | undefined
  isAnalysisPending: boolean
  isAnalysisFailed: boolean
  isAnalysisUnavailable: boolean
  isError: boolean
  isRefetchError: boolean
  progress: AnalysisProgress
}

export type UseCIFindingsOptions = {
  enabled?: boolean
  pollingTimeoutMs?: number
  apiUrl?: string
  headSha?: string
}

export function useCIFindings({
  enabled = true,
  pollingTimeoutMs = MAX_POLL_DURATION_MS,
  apiUrl: apiUrlOverride,
  headSha: headShaOverride,
}: UseCIFindingsOptions = {}): CIFindingsState {
  const pageDataUrl = usePageDataUrl(PageData.ciFindings)
  const apiURL = apiUrlOverride ?? pageDataUrl
  // The interval callback closes over the timeout value assigned after the query reports its error state.
  let pollingExpired = false

  const {data, isError, isRefetchError} = useQuery({
    queryKey: ['ciFindings', apiURL, headShaOverride],
    queryFn: async (): Promise<CIFindingsData> => {
      const response = await fetchWithErrorHandling(apiURL)
      if (!response.ok && response.status >= 500) {
        throw new ServerUnavailableError(response.status)
      }
      const json = (await parseJSONWithBetterErrors(response)) as CIFindingsResponse & {error?: string}
      throwErrorsIfBadResponse(response, json)
      return adaptCIFindingsResponse(json)
    },
    structuralSharing: (previous, current) =>
      replaceEqualDeep(
        previous,
        mergeCIFindingsData(previous as CIFindingsData | undefined, current as CIFindingsData),
      ),
    staleTime: TERMINAL_REFETCH_INTERVAL_MS,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return (
        error instanceof ServerUnavailableError ||
        error.name === 'FetchRequestError' ||
        error.name === 'JSONParseFetchError' ||
        error.name === 'TypeError'
      )
    },
    enabled,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === apiURL && previousQuery.queryKey[2] === headShaOverride
        ? keepPreviousData(previousData)
        : undefined,
    refetchInterval: query => {
      if (query.state.status === 'error' || pollingExpired) return false
      if (shouldPollForCIFindings(query.state.data, pollingExpired)) return POLL_INTERVAL_MS
      return hasNormalizedLifecycle(query.state.data) ? TERMINAL_REFETCH_INTERVAL_MS : false
    },
  })

  const shouldPoll = enabled && !isError
  pollingExpired = useAnalysisPollingTimeout({
    apiURL,
    headSha: headShaOverride ?? data?.headSha,
    enabled: shouldPoll,
    timeoutMs: pollingTimeoutMs,
  })

  useEffect(() => {
    if (isError) {
      sendStats({incrementKey: 'CI_FINDINGS_FETCH_ERROR'})
    }
  }, [isError])

  const currentData = data ?? EMPTY_DATA
  const rawFindings = currentData.findings
  const checkRunsForAnalysis = currentData.checkRunsForAnalysis ?? EMPTY_CHECK_RUNS
  const progress: AnalysisProgress = useMemo(
    () => computeAnalysisProgress(rawFindings, checkRunsForAnalysis),
    [checkRunsForAnalysis, rawFindings],
  )
  const analysisPending = isCIFindingsAnalysisPending(data, progress)
  const isAnalysisFailed = hasNormalizedLifecycle(data) && data.jobStatus === 'failed'
  const isAnalysisUnavailable = shouldPoll && pollingExpired && analysisPending
  const isAnalysisPending = (shouldPoll || isRefetchError) && !pollingExpired && analysisPending
  const findings = useMemo(() => deduplicateCIFindings(rawFindings), [rawFindings])

  return {
    findings,
    headSha: data?.headSha ?? headShaOverride,
    isAnalysisPending,
    isAnalysisFailed,
    isAnalysisUnavailable,
    isError,
    isRefetchError,
    progress,
  }
}
